import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBed, faBath, faRulerCombined } from "@fortawesome/free-solid-svg-icons";
import { motion, useScroll, useTransform } from "framer-motion";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { Helmet } from "react-helmet-async";
import seoData from "../assets/data/seo.json";
import heroImg from "../assets/realestate/hero.png";
import prop1 from "../assets/realestate/prop1.png";
import prop2 from "../assets/realestate/prop2.png";
import prop3 from "../assets/realestate/prop3.png";

// Reusing assets for stories
// removed stories array since it is no longer used
const FEATURED_PROPERTIES = [
  {
    id: 1,
    image: prop1,
    title: "Modern Minimalist Villa",
    location: "Beverly Hills, CA",
    price: "$4,500,000",
    beds: 5,
    baths: 6,
    sqft: "6,200",
    type: "For Sale"
  },
  {
    id: 2,
    image: prop2,
    title: "Luxury Skyline Penthouse",
    location: "Manhattan, NY",
    price: "$8,250,000",
    beds: 3,
    baths: 4,
    sqft: "4,100",
    type: "For Sale"
  },
  {
    id: 3,
    image: prop3,
    title: "Upscale Suburban Estate",
    location: "Hamptons, NY",
    price: "$3,100,000",
    beds: 6,
    baths: 5,
    sqft: "5,800",
    type: "For Sale"
  }
];

const Home = () => {
  // Animation hooks for Section 2 (Art of Detail)
  const section2Ref = useRef(null);
  const { scrollYProgress: section2ScrollY } = useScroll({
    target: section2Ref,
    offset: ["start end", "start start"]
  });

  const section2BorderRadius = useTransform(
    section2ScrollY,
    [0, 0.8, 1],
    ["1000px", "1000px", "0px"]
  );

  const section2Y = useTransform(
    section2ScrollY,
    [0, 1],
    [120, 0]
  );

  // Animation hooks for Section 3 (Properties Slider)
  const section3Ref = useRef(null);
  const { scrollYProgress: section3ScrollY } = useScroll({
    target: section3Ref,
    offset: ["start 75%", "start 20%"]
  });
  const section3Scale = useTransform(section3ScrollY, [0, 1], [0.7, 1]);

  const slickSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 4000,
  };

  const storiesSlickSettings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    responsive: [
      { breakpoint: 1024, settings: { slidesToShow: 2 } },
      { breakpoint: 640, settings: { slidesToShow: 1 } }
    ]
  };

  const seo = seoData["home"] || {};
  const { title, description, keywords, canonical, ogImage } = seo;

  return (
    <div className="min-h-screen bg-white font-sans">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        {keywords && <meta name="keywords" content={keywords} />}
        {canonical && <link rel="canonical" href={canonical} />}
        {ogImage && <meta property="og:image" content={ogImage} />}
      </Helmet>

      {/* SECTION 1: Hero Banner */}
      <section className="sticky top-0 h-screen w-full overflow-hidden flex items-center z-10">
        <div className="absolute inset-0 z-0">
          <img src={heroImg} alt="Luxury Real Estate" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/40" />
        </div>

        <div className="container mx-auto px-6 relative z-10 text-center mt-20">
          <h2 className="font-light text-white mb-4 tracking-wider uppercase drop-shadow-lg text-[26px] md:text-[48px]">
            Jetty Estates
          </h2>
          <p className="text-white font-light mb-10 max-w-2xl mx-auto tracking-widest uppercase drop-shadow-md text-[12px] md:text-[14px] lg:text-[16px]">
            Spaces Where Life Unfolds
          </p>
          <Link to="/properties" className="inline-block px-10 py-3 border border-white text-white font-bold hover:bg-white hover:text-black transition-all uppercase tracking-widest text-sm bg-black/20 backdrop-blur-sm">
            Discover
          </Link>
        </div>
      </section>

      {/* SECTION 2: Art of Detail (Animated) */}
      <div ref={section2Ref} className="relative z-20 -mt-20">
        <motion.div
          style={{
            y: section2Y,
            borderTopLeftRadius: section2BorderRadius,
            borderTopRightRadius: section2BorderRadius,
          }}
          className="bg-white min-h-screen w-full overflow-hidden shadow-2xl"
        >
          <section className="min-h-screen flex flex-col justify-center items-center px-6 text-black pt-32 pb-20">
          <div className="relative z-10 text-center max-w-4xl mx-auto px-6 flex flex-col items-center">
            {/* The Logo / Typography */}
            <h2 className="font-bold mb-2 uppercase tracking-widest text-[26px] md:text-[48px]">
              The Art
            </h2>
            <h3 className="font-light italic mb-12 flex items-center gap-4 text-[24px] md:text-[30px]">
              <span className="font-serif lowercase text-[14px]">of</span> <span className="uppercase tracking-widest font-bold text-[14px]">Detail</span>
            </h3>

            <p className="font-light leading-relaxed mb-10 text-gray-700 max-w-3xl text-[12px] md:text-[14px] lg:text-[16px]">
              At Jetty Estates, we understand that true excellence lies in the meticulous attention to detail and the artistry of craftsmanship. Guided by a commitment to perfection, we believe in crafting not just homes but immersive experiences where every nuance is thoughtfully considered.
            </p>
            <Link to="/aboutus" className="inline-block px-10 py-4 border border-black text-black font-bold hover:bg-black hover:text-white transition-all uppercase tracking-widest text-sm rounded-full">
              Discover More
            </Link>
          </div>
          </section>
        </motion.div>
      </div>

      {/* SECTION 3: Luxury Properties and Pillars */}
      <section className="bg-white py-20 overflow-hidden relative z-10">
        <div className="container mx-auto px-6 mb-12 text-center flex items-center justify-center">
          <div className="h-px bg-gray-300 w-12 mr-6 hidden md:block"></div>
          <h2 className="font-light text-black uppercase tracking-wider m-0 text-[26px] md:text-[48px]">
            Explore Our Luxury Properties
          </h2>
          <div className="h-px bg-gray-300 w-12 ml-6 hidden md:block"></div>
        </div>

        {/* Properties Slider with scale up on scroll */}
        <div ref={section3Ref} className="w-full max-w-[1600px] mx-auto mb-32 px-4">
          <motion.div style={{ scale: section3Scale, transformOrigin: "bottom center" }}>
            <Slider {...slickSettings} className="mb-10">
              {FEATURED_PROPERTIES.map((property) => (
                <div key={property.id} className="relative h-[70vh] min-h-[500px] outline-none group cursor-pointer">
                  <div className="absolute inset-0 mx-2 overflow-hidden bg-gray-100">
                    <img src={property.image} alt={property.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    
                    {/* Bottom Info Bar styled like Sobha */}
                    <div className="absolute bottom-0 left-0 right-0 bg-white/95 backdrop-blur-md p-5 flex flex-col md:flex-row justify-between items-center border-t border-gray-200">
                      {/* Left: Title acting as Logo placeholder */}
                      <div className="mb-3 md:mb-0 text-center md:text-left">
                        <h3 className="font-medium uppercase tracking-widest text-black text-[24px] md:text-[30px]">{property.title}</h3>
                        <p className="text-gray-500 uppercase tracking-widest mt-1 text-[12px] md:text-[14px] lg:text-[16px]">{property.location}</p>
                      </div>
                      
                      {/* Right: Amenities */}
                      <div className="flex gap-6 items-center">
                        <div className="flex items-center gap-2">
                          <FontAwesomeIcon icon={faBed} className="text-gray-400" />
                          <span className="font-light uppercase text-gray-800 text-[14px]">{property.beds} Beds</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <FontAwesomeIcon icon={faBath} className="text-gray-400" />
                          <span className="font-light uppercase text-gray-800 text-[14px]">{property.baths} Baths</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <FontAwesomeIcon icon={faRulerCombined} className="text-gray-400" />
                          <span className="font-light uppercase text-gray-800 text-[14px]">{property.sqft} Sqft</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </Slider>
          </motion.div>
        </div>

        {/* Defining Pillars */}
        <div className="container mx-auto px-6 mt-32 max-w-7xl">
          <div className="text-center flex items-center justify-center mb-12">
            <div className="h-px bg-gray-300 w-12 mr-6 hidden md:block"></div>
            <h2 className="font-light text-black uppercase tracking-wider m-0 text-[26px] md:text-[48px]">
              From Concept To Completion: Defining Our Pillars
            </h2>
            <div className="h-px bg-gray-300 w-12 ml-6 hidden md:block"></div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
          >
            <Slider {...storiesSlickSettings} className="gap-6 -mx-3">
              {[
                { title: "Craftsmanship", desc: "When building a house, attention to detail is essential. Jetty Estates recognises this, and we pay attention to every detail, whether it’s the quality of the materials, textures and finishes, door hinges, doors, floor tiling, bathroom flooring, fittings or window size. A true craftsman for a harmonious life.", img: prop1 },
                { title: <>Thoughtful<br/>Design</>, desc: "At Jetty Estates, home design is not just a structure; it’s a beautiful piece of art. Homes that are spacious with well-utilised spaces. The design is carefully considered and subjected to severe quality inspections. We consider every stage of construction to ensure a thoughtful design.", img: prop2 },
                { title: <>Signature<br/>Quality</>, desc: "At Jetty Estates, we are involved in every stage of the process, from material sourcing to building homes. Every home built by us is subjected to a material quality inspection. All of the items used in the construction are manufactured by us, allowing complete control over the quality of the products utilised.", img: prop3 }
              ].map((feature, i) => (
                <div key={i} className="px-3 outline-none">
                  <div className="group cursor-pointer">
                    <div className="w-full h-[400px] overflow-hidden mb-6 bg-gray-100">
                      <img src={feature.img} alt="Pillar" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    </div>
                    <div className="pr-4">
                      <h4 className="font-medium text-black mb-3 leading-tight text-[20px]">{feature.title}</h4>
                      <p className="font-light text-gray-600 leading-relaxed text-[12px] md:text-[14px] lg:text-[16px]">{feature.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </Slider>
          </motion.div>
        </div>
      </section>

   

    </div>
  );
};

export default Home;
